import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ChangeRequestApprove() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const [req, setReq] = useState(null);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/change-requests/by-token/${token}`).then(r => setReq(r.data))
      .catch(() => toast.error("Invalid link"));
  }, [token]);

  useEffect(() => {
    if (loading) return;
    if (!user) nav(`/admin/login?next=/admin/approve/${token}`);
    else if (user.role !== "admin") {
      toast.error("Only admin can approve");
      nav("/");
    }
  }, [user, loading, token]);

  const decide = async (d) => {
    setBusy(true);
    try {
      await api.patch(`/change-requests/by-token/${token}`, { decision: d, admin_note: note });
      setDecision(d);
      toast.success(`Request ${d}`);
    } catch (err) {
      const e = err?.response?.data?.detail;
      toast.error(typeof e === "string" ? e : "Failed");
    } finally { setBusy(false); }
  };

  if (!req) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading change request…</div>;

  return (
    <div className="min-h-screen bg-[var(--bg)] py-12 px-4 md:px-6" data-testid="cr-approve-page">
      <div className="max-w-3xl mx-auto bg-white border border-[var(--border)] p-8 md:p-12">
        <p className="uppercase text-xs tracking-[0.3em] text-[var(--accent)] mb-3">Change Request · Admin Decision</p>
        <h1 className="font-display text-4xl mb-2">{req.title}</h1>
        <p className="text-xs text-[var(--muted)] mb-8">Submitted by {req.submitted_by_name} ({req.submitted_by_role}) · {new Date(req.created_at).toLocaleString()}</p>

        <div className="space-y-5 text-sm">
          <Section label="Description">{req.description}</Section>
          <Section label="Impact">{req.impact}</Section>
          <Section label="Estimated Downtime">{req.estimated_downtime}</Section>
          {!!(req.affected_collections || []).length && <Section label="Affected DB Collections">{req.affected_collections.join(", ")}</Section>}
          {req.proposed_changes && Object.keys(req.proposed_changes).length > 0 && (
            <Section label="Proposed Changes">
              <pre className="bg-[var(--bg)] p-4 text-xs overflow-x-auto border border-[var(--border)]">{JSON.stringify(req.proposed_changes, null, 2)}</pre>
            </Section>
          )}
        </div>

        {req.status !== "pending" || decision ? (
          <div className="mt-8 p-5 border border-[var(--border)] bg-[var(--bg)]" data-testid="cr-decided">
            <p className="text-sm">
              <strong className="uppercase tracking-widest text-xs">Status:</strong> {decision || req.status}
              {req.admin_note && <> · Admin note: <em>{req.admin_note}</em></>}
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Optional admin note"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5" data-testid="cr-note" />
            <div className="flex gap-3 justify-end">
              <button disabled={busy} onClick={() => decide("declined")} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-7 py-3 text-xs tracking-widest uppercase" data-testid="cr-decline">Decline</button>
              <button disabled={busy} onClick={() => decide("approved")} className="btn-gold rounded-full px-7 py-3 text-xs tracking-widest uppercase" data-testid="cr-approve">Approve</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-widest text-[var(--muted)] mb-1">{label}</p>
    <div className="text-[var(--text)]">{children}</div>
  </div>
);
